/**
 * Month Range Calendar Component
 * Similar design to DateRangeCalendar but for selecting month ranges
 * Uses UTC dates throughout to avoid timezone issues
 */

import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';

interface MonthRangeCalendarProps {
  startDate?: Date;
  endDate?: Date;
  minDate: Date;
  maxDate: Date;
  onRangeChange: (start?: Date, end?: Date) => void;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

// UTC helper functions
const startOfMonthUTC = (year: number, month: number): Date => {
  return new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
};

const endOfMonthUTC = (year: number, month: number): Date => {
  // Get last day of month by going to first day of next month and subtracting 1 day
  return new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));
};

const MonthRangeCalendar: React.FC<MonthRangeCalendarProps> = ({
  startDate,
  endDate,
  minDate,
  maxDate,
  onRangeChange,
}) => {
  const [displayYear, setDisplayYear] = React.useState(() => {
    return endDate?.getUTCFullYear() || startDate?.getUTCFullYear() || new Date().getUTCFullYear();
  });

  const [rangeStart, setRangeStart] = React.useState<Date | undefined>(startDate);
  const [rangeEnd, setRangeEnd] = React.useState<Date | undefined>(endDate);
  const [selectingEnd, setSelectingEnd] = React.useState(false);

  // Sync with props
  React.useEffect(() => {
    setRangeStart(startDate);
    setRangeEnd(endDate);
  }, [startDate, endDate]);

  const minYear = minDate.getUTCFullYear();
  const maxYear = maxDate.getUTCFullYear();

  const canGoPrev = displayYear > minYear;
  const canGoNext = displayYear < maxYear;

  const handlePrevYear = () => {
    if (canGoPrev) {
      setDisplayYear(displayYear - 1);
    }
  };

  const handleNextYear = () => {
    if (canGoNext) {
      setDisplayYear(displayYear + 1);
    }
  };

  const isMonthDisabled = (monthIndex: number): boolean => {
    const monthStart = startOfMonthUTC(displayYear, monthIndex);
    const monthEnd = endOfMonthUTC(displayYear, monthIndex);
    const minStart = startOfMonthUTC(minDate.getUTCFullYear(), minDate.getUTCMonth());
    const maxEnd = endOfMonthUTC(maxDate.getUTCFullYear(), maxDate.getUTCMonth());
    return monthEnd < minStart || monthStart > maxEnd;
  };

  const isMonthInRange = (monthIndex: number): boolean => {
    if (!rangeStart || !rangeEnd) return false;
    const monthStart = startOfMonthUTC(displayYear, monthIndex);
    return monthStart > rangeStart && monthStart < rangeEnd;
  };

  const isRangeStart = (monthIndex: number): boolean => {
    if (!rangeStart) return false;
    return rangeStart.getUTCFullYear() === displayYear && rangeStart.getUTCMonth() === monthIndex;
  };

  const isRangeEnd = (monthIndex: number): boolean => {
    if (!rangeEnd) return false;
    return rangeEnd.getUTCFullYear() === displayYear && rangeEnd.getUTCMonth() === monthIndex;
  };

  const isCurrentMonth = (monthIndex: number): boolean => {
    const now = new Date();
    return now.getUTCFullYear() === displayYear && now.getUTCMonth() === monthIndex;
  };

  const handleMonthClick = (monthIndex: number) => {
    if (isMonthDisabled(monthIndex)) return;

    const clickedDate = startOfMonthUTC(displayYear, monthIndex);

    if (!selectingEnd || !rangeStart) {
      // Selecting start
      setRangeStart(clickedDate);
      setRangeEnd(undefined);
      setSelectingEnd(true);
      onRangeChange(clickedDate, undefined);
    } else {
      // Selecting end
      if (clickedDate < rangeStart) {
        // Clicked before start, swap
        const newEnd = endOfMonthUTC(rangeStart.getUTCFullYear(), rangeStart.getUTCMonth());
        setRangeEnd(newEnd);
        setRangeStart(clickedDate);
        setSelectingEnd(false);
        onRangeChange(clickedDate, newEnd);
      } else {
        const newEnd = endOfMonthUTC(displayYear, monthIndex);
        setRangeEnd(newEnd);
        setSelectingEnd(false);
        onRangeChange(rangeStart, newEnd);
      }
    }
  };

  const formatSelectedRange = () => {
    if (rangeStart && rangeEnd) {
      return `${formatInTimeZone(rangeStart, 'UTC', 'MMM yyyy')} - ${formatInTimeZone(rangeEnd, 'UTC', 'MMM yyyy')}`;
    } else if (rangeStart) {
      return `${formatInTimeZone(rangeStart, 'UTC', 'MMM yyyy')} - Select end month`;
    }
    return 'Select start month';
  };

  const getMonthClasses = (monthIndex: number): string => {
    const disabled = isMonthDisabled(monthIndex);
    const inRange = isMonthInRange(monthIndex);
    const start = isRangeStart(monthIndex);
    const end = isRangeEnd(monthIndex);
    const current = isCurrentMonth(monthIndex);
    const singleSelection = start && end;

    let classes = 'month-button';

    if (disabled) {
      classes += ' disabled';
    } else {
      if (start || end) {
        classes += ' selected';
        if (start && !singleSelection) classes += ' range-start';
        if (end && !singleSelection) classes += ' range-end';
      } else if (inRange) {
        classes += ' range-middle';
      }
      if (current) {
        classes += ' today';
      }
    }

    return classes;
  };

  return (
    <div className="month-range-calendar">
      <style>{`
        .month-range-calendar {
          --rdp-cell-size: 32px;
          --rdp-accent-color: var(--primary);
          margin: 0;
        }

        .month-range-calendar .header {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          margin-bottom: 1rem;
          height: 28px;
        }

        .month-range-calendar .year-label {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--foreground);
          text-align: center;
        }

        .month-range-calendar .nav-buttons {
          position: absolute;
          left: 0;
          right: 0;
          display: flex;
          justify-content: space-between;
          pointer-events: none;
        }

        .month-range-calendar .nav-button {
          width: 28px;
          height: 28px;
          padding: 0;
          border-radius: 8px;
          border: none;
          background: transparent;
          cursor: pointer;
          color: var(--foreground);
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
        }

        .month-range-calendar .nav-button:hover:not(:disabled) {
          background-color: var(--background-secondary);
        }

        .month-range-calendar .nav-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .month-range-calendar .nav-button svg {
          width: 20px;
          height: 20px;
          color: white;
          stroke: white;
          fill: white;
        }

        .month-range-calendar .months-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }

        .month-range-calendar .month-button {
          height: 40px;
          border-radius: 9999px;
          border: none;
          background: transparent;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--foreground);
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .month-range-calendar .month-button:hover:not(.disabled) {
          background-color: rgba(59, 130, 246, 0.2);
        }

        .month-range-calendar .month-button.today {
          box-shadow: inset 0 0 0 1px var(--primary);
        }

        .month-range-calendar .month-button.disabled {
          color: var(--text-muted);
          opacity: 0.3;
          cursor: not-allowed;
        }

        .month-range-calendar .month-button.disabled:hover {
          background: transparent;
        }

        .month-range-calendar .month-button.selected {
          background-color: var(--primary) !important;
          color: white !important;
        }

        .month-range-calendar .month-button.selected.today {
          box-shadow: none;
        }

        .month-range-calendar .month-button.range-start {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        .month-range-calendar .month-button.range-end {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }

        .month-range-calendar .month-button.range-middle {
          background-color: rgba(59, 130, 246, 0.2) !important;
          color: var(--foreground) !important;
          border-radius: 0;
        }

        .month-range-calendar .month-button.range-middle:hover {
          background-color: rgba(59, 130, 246, 0.3) !important;
        }
      `}</style>

      <div className="header">
        <span className="year-label">{displayYear}</span>
        <div className="nav-buttons">
          <button
            type="button"
            className="nav-button"
            onClick={handlePrevYear}
            disabled={!canGoPrev}
            aria-label="Previous year"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className="nav-button"
            onClick={handleNextYear}
            disabled={!canGoNext}
            aria-label="Next year"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>

      <div className="months-grid">
        {MONTHS.map((month, index) => (
          <button
            key={month}
            type="button"
            className={getMonthClasses(index)}
            onClick={() => handleMonthClick(index)}
            disabled={isMonthDisabled(index)}
          >
            {month}
          </button>
        ))}
      </div>

      {/* Selected Range Display */}
      <div className="mt-4 pt-3 border-t border-border">
        <p className="text-sm text-center mb-2">
          <span className="text-text-muted">Selected: </span>
          <span className="text-foreground font-medium">{formatSelectedRange()}</span>
        </p>
        <p className="text-xs text-text-muted text-center">
          Data available: {formatInTimeZone(minDate, 'UTC', 'MMMM yyyy')} - {formatInTimeZone(maxDate, 'UTC', 'MMMM yyyy')}
        </p>
      </div>
    </div>
  );
};

export default MonthRangeCalendar;
